export interface TourStep {
  target: string;
  title: string;
  content: string;
  routeBefore?: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  spotlightClicks?: boolean;
}

export interface TourDefinition {
  id: string;
  name: string;
  description: string;
  steps: TourStep[];
  startRoute: string;
}

export interface TourProgress {
  lastStep: number;
  completed: boolean;
}

export type TourProgressMap = Record<string, TourProgress>;

export interface TourSelectionModalProps {
  open: boolean;
  onClose: () => void;
  /** startStep is passed when resuming a previously exited tour (US4 / T008) */
  onSelectTour: (tour: TourDefinition, startStep?: number) => void;
  userId: number;
}

export interface TourRunnerProps {
  tour: TourDefinition | null;
  userId: number;
  startStep?: number;
  onComplete: () => void;
  onExit: () => void;
}

export const TOURS: TourDefinition[] = [
  {
    id: 'create-course',
    name: 'Creating a Course',
    description: 'Learn how to create a new course and set it up for your students.',
    startRoute: '/tutor/courses',
    steps: [
      {
        target: 'body',
        placement: 'center',
        title: 'Welcome to My Courses',
        content:
          'This is your courses page. All the courses you create and manage appear here. Archived courses are hidden but can be accessed via the Archived Courses tab.',
      },
      {
        target: 'button[aria-label="Create Course"], button:contains("Create Course"), [data-testid="create-course-btn"]',
        placement: 'bottom',
        title: 'Create a New Course',
        content:
          'Click "Create Course" to start building a new course. You\'ll choose a curriculum and give your course a name. Your course will appear in this list once created.',
      },
      {
        target: '.MuiCard-root, [data-testid="course-card"]',
        placement: 'bottom',
        title: 'Your Course Cards',
        content:
          'Each course appears as a card showing its name and status. Click "Manage" on any card to open the course and start adding topics, quizzes, and students.',
      },
    ],
  },
  {
    id: 'course-structure',
    name: 'Managing Course Structure',
    description: 'Add topics and subtopics to organise your course content.',
    startRoute: '/tutor/courses/:firstCourseId/structure',
    steps: [
      {
        target: 'body',
        placement: 'center',
        title: 'Course Structure Overview',
        content:
          'This is the Structure page for your course. Here you can organise your content into topics and subtopics, building the learning pathway your students will follow.',
        routeBefore: '/tutor/courses/:firstCourseId/structure',
      },
      {
        target: 'h1, h2, h4, [data-testid="structure-heading"]',
        placement: 'bottom',
        title: 'Topics and Subtopics',
        content:
          'Topics are the main chapters of your course. Each topic can have multiple subtopics. Students progress through topics in order, completing quizzes and activities as they go.',
      },
      {
        target: 'button[aria-label="Add Topic"], [data-testid="add-topic-btn"], button:last-of-type',
        placement: 'top',
        title: 'Adding a Topic',
        content:
          'Click "Add Topic" to create a new chapter. Give it a clear name — your students will see this. You can reorder topics by dragging them once created.',
      },
    ],
  },
  {
    id: 'registration-codes',
    name: 'Adding Students via Registration Codes',
    description: 'Share a unique code so students can self-enrol in your course.',
    startRoute: '/tutor/courses/:firstCourseId/codes',
    steps: [
      {
        target: 'body',
        placement: 'center',
        title: 'Registration Codes',
        content:
          'Every course has a unique registration code. Students enter this code during sign-up or from their dashboard to instantly enrol in your course.',
        routeBefore: '/tutor/courses/:firstCourseId/codes',
      },
      {
        target: '[data-testid="course-code"], .MuiChip-root, code, pre',
        placement: 'bottom',
        title: 'Your Course Code',
        content:
          'This is your course\'s unique code. Share it with your students via email, a class handout, or any messaging tool. The code is case-insensitive.',
      },
      {
        target: 'button[aria-label="Copy"], [data-testid="copy-code-btn"], button:contains("Copy")',
        placement: 'bottom',
        title: 'Copying the Code',
        content:
          'Use the copy button to copy the code to your clipboard instantly. You can also generate a new code at any time — the old code stops working when you regenerate.',
      },
    ],
  },
  {
    id: 'quiz-assessments',
    name: 'Creating Quizzes and Assessments',
    description: 'Build quizzes to test your students\' understanding.',
    startRoute: '/tutor/courses/:firstCourseId/quiz',
    steps: [
      {
        target: 'body',
        placement: 'center',
        title: 'Quizzes and Assessments',
        content:
          'This section lets you create quizzes attached to course topics. Students take quizzes to demonstrate their understanding before moving on.',
        routeBefore: '/tutor/courses/:firstCourseId/quiz',
      },
      {
        target: 'h1, h2, h4, [data-testid="quiz-heading"]',
        placement: 'bottom',
        title: 'Quiz Overview',
        content:
          'Each quiz is linked to a topic in your course. You can set the number of questions, pass mark, and whether students can retake the quiz.',
      },
      {
        target: 'button[aria-label="Add Quiz"], [data-testid="add-quiz-btn"], button:last-of-type',
        placement: 'top',
        title: 'Creating a Quiz',
        content:
          'Click to add a new quiz. You\'ll write the questions and provide answer choices. Once published, the quiz becomes visible to all enrolled students.',
      },
    ],
  },
  {
    id: 'student-roster',
    name: 'Viewing and Managing the Student Roster',
    description: 'See who is enrolled and track their progress.',
    startRoute: '/tutor/courses/:firstCourseId/roster',
    steps: [
      {
        target: 'body',
        placement: 'center',
        title: 'Student Roster',
        content:
          'The Roster page shows every student currently enrolled in this course. You can see their progress, completed topics, and quiz scores at a glance.',
        routeBefore: '/tutor/courses/:firstCourseId/roster',
      },
      {
        target: 'table, .MuiTableContainer-root, [data-testid="roster-table"]',
        placement: 'top',
        title: 'Student Progress',
        content:
          'Each row shows a student\'s name, enrolment date, topics completed, and overall progress. Use this to identify students who may need extra support.',
      },
      {
        target: 'button[aria-label="Remove"], [data-testid="remove-student-btn"], .MuiIconButton-root:last-child',
        placement: 'left',
        title: 'Managing Students',
        content:
          'You can remove a student from your course if needed. They lose access immediately but their progress history is retained. Students can re-enrol using the course code.',
      },
    ],
  },
  {
    id: 'archive-restore',
    name: 'Archiving and Restoring Courses',
    description: 'Keep your course list tidy by archiving courses you no longer actively teach.',
    startRoute: '/tutor/courses',
    steps: [
      {
        target: 'body',
        placement: 'center',
        title: 'Archiving a Course',
        content:
          'Archiving removes a course from your active list without deleting it. Enrolled students lose access to the course when it is archived.',
      },
      {
        target: '[data-testid="course-menu"], button[aria-label="Course options"], .MuiIconButton-root',
        placement: 'left',
        title: 'Course Options Menu',
        content:
          'Click the options menu (⋮) on any course card to reveal the Archive action. You can also edit the course name and manage other settings from this menu.',
      },
      {
        target: '[data-testid="archived-tab"], button:contains("Archived"), a[href*="archived"]',
        placement: 'bottom',
        title: 'Restoring Archived Courses',
        content:
          'Switch to the "Archived Courses" tab to see all your archived courses. Click "Restore" on any course to make it active again and re-enable student access.',
      },
    ],
  },
];
